/// <reference types="@types/facebook-pixel" />
import type { Tracker } from '../../types';
import {
    awaitAdvertisingPixels,
    createMissingPixelWarning,
} from '../advertising-pixels';
import { deriveMetaEvents } from './deriveMetaEvents';
import { isServerForwardedEvent } from './serverForwardedEvents';

declare global {
    // @types/facebook-pixel stops at three arguments. The fourth carries the
    // deduplication id that pairs this browser event with its server-side
    // Conversions API copy.
    namespace facebook.Pixel {
        interface Event {
            (
                eventType: string,
                eventName: string,
                parameters: CustomParameters,
                options: { eventID: string },
            ): void;
        }
    }
}

export function createFacebookTracker(): Tracker {
    const warnPixelMissing = createMissingPixelWarning('Meta');

    return {
        name: 'facebookTracker',
        async log(event, opts) {
            await awaitAdvertisingPixels();

            const metaEvents = deriveMetaEvents(event, {
                clientMatched: Boolean(opts.clientMatched),
            });
            if (metaEvents.length === 0) return;

            const [baseEvent] = metaEvents;

            if (typeof fbq === 'undefined') {
                warnPixelMissing(baseEvent.eventName);
                return;
            }

            // eventID lets Meta collapse this event and the server-side
            // Conversions API copy into a single conversion. Sent only for the
            // events we actually forward, so that carrying an id means a server
            // copy is coming — a disagreement between the two sides would
            // double-count the conversion silently.
            //
            // Decided on the base event because the forwarder derives the rest
            // of the set from it: either all of them get a server copy or none
            // of them do.
            const deduplicate =
                Boolean(opts.eventId) &&
                isServerForwardedEvent(baseEvent.eventName);

            // Sent on the browser copy as well as the server one. Meta keeps a
            // single copy of each event after deduplication, so a parameter
            // present on only one side is reported inconsistently.
            const parameters = withProfessionalId(
                opts.data,
                opts.professionalId,
            );

            // @todo map the customData object against valid Facebook parameters.
            for (const { eventName, type } of metaEvents) {
                console.log(
                    '%cFacebookEvent: ' + eventName + ' (' + type + ')',
                    'background: #222; color: #bada55',
                );

                const endpoint = type === 'standard' ? 'track' : 'trackCustom';

                if (deduplicate) {
                    fbq(endpoint, eventName, parameters ?? {}, {
                        eventID: opts.eventId as string,
                    });
                } else if (parameters) {
                    fbq(endpoint, eventName, parameters);
                } else {
                    fbq(endpoint, eventName);
                }
            }
        },
        release() {
            // do nothing
        },
    };
}

/**
 * Adds the conversion's professional to the pixel parameters so the ads team can
 * segment by client in Events Manager without relying on the event name.
 */
function withProfessionalId(
    data: Record<string, unknown> | undefined,
    professionalId: number | undefined,
): Record<string, unknown> | undefined {
    if (professionalId === undefined) {
        return data;
    }

    return { ...data, professional_id: professionalId };
}
